export type Stack = {
  framework: 'nextjs' | 'astro' | 'sveltekit' | 'remix' | 'vue'
}

export const getWebsiteCreationPrompt = ({
  stack,
  token,
  structureXml,
}: {
  stack: Stack
  token: string
  structureXml: string
}) => {
  return `Create a website using BaseHub as my CMS. This is the current structure of my content repository:

\`\`\`xml
${structureXml}
\`\`\`

You should understand what I'm building based on the provided content structure. I'm using ${stack.framework} as my framework. To connect to BaseHub:

1. Install \`basehub\` from npm.
2. Add \`BASEHUB_TOKEN\` to the environment variables. The token is: \`${token}\`.
3. (optional, for type generation) Add \`basehub dev\` to your "dev" script in \`package.json\` (run this in parallel using \`concurrently\`, as it'll watch for schema changes) and \`basehub build\` to your "build" script (in this case, don't run it in parallel, but rather, before your regular build step).
4. Integrate:
${INTEGRATION_SNIPPETS[stack.framework].snippets.map(({ code, filename }) => {
  return `\`\`\`${filename}\n${typeof code === 'function' ? code() : code}\n\`\`\``
})}

BaseHub exposes a GraphQL API, and the \`basehub\` SDK is a TypeScript client for it (based on Genql). The schema is generated dynamically depending on the repository structure. Things to keep in mind:

1. List/collection rows are queried by accessing \`items\` and can be filtered by an argument called \`filter\`. E.g:

\`\`\`ts (genql)
import { basehub } from 'basehub'

basehub().query({
  someCollection: {
    __args: {
      filter: {
        _slug: { eq: 'some-slug' },
        publishedAt: { isAfter: 'some-date-iso' },
      },
      first: 1,
      skip: 5,
      orderBy: 'publishedAt__DESC',
    },
    items: { _title: true, _id: true, _slug: true, publishedAt: true },
  },
  _meta: { totalCount: true, filteredCount: true },
})
 \`\`\`

 2. OG Image blocks just have a url key below, like: \`someOgImage: { url: true }\`
 3. Event blocks don't yet return their value / current state. Instead, they return an \`ingestKey\` and an \`adminKey\`, which can then be used in conjunction to \`sendEvent\` and \`queryEvent\`. Like:

\`\`\`ts
import { sendEvent, parseFormData } from 'basehub/events'

const { newsletter } = await basehub().query({ newsletter: { ingestKey: true } })

// later on, passed to the client

const onSubmit = (formData) => {
  const parsedSubmission = parseFormData(
    ingestKey,
    homepage.submissions.schema,
    formData,
  )

  if (!parsedSubmission.success) {
    // The \`parseFormData\` return type lets you
    // handle parsing errors. Since this is a simple
    // example, we're just throwing an error.
    throw new Error(
      JSON.stringify(parsedSubmission.errors),
    )
  }
  sendEvent(newsletter.ingestKey, parsedSubmission.data)
}
\`\`\`

`
}

const INTEGRATION_SNIPPETS = {
  nextjs: {
    selectValue: 'Next.js',
    language: 'tsx',
    snippets: [
      {
        filename: 'app/page.tsx',
        code: () => `import { Pump } from 'basehub/react-pump'
  import { draftMode } from 'next/headers'
  
  const Page = async () => {
    return (
      <Pump queries={[{ __typename: true }]}>
        {async ([data]) => {
          'use server'
          return <>{JSON.stringify(data, null, 2)}</>
        }}
      </Pump>
    )
  }
  
  export default Page`,
      },
    ],
  },
  astro: {
    selectValue: 'Astro',
    language: 'tsx',
    snippets: [
      {
        filename: 'src/pages/blog/index.astro',
        code: (recommended?: string) => `---
  import { basehub } from "basehub"
  
  ${addSpacesToCodeBlock(recommended || exampleQuery, 0)}
  ---
  
  <!DOCTYPE html>
  <html lang="en">
    <head>
      <title>My Website</title>
    </head>
    <body>
      <pre>{JSON.stringify(data, null, 2)}</pre>
    </body>
  </html>`,
      },
    ],
  },
  sveltekit: {
    selectValue: 'SvelteKit',
    language: 'typescript',
    snippets: [
      {
        filename: 'src/routes/+page.server.ts',
        code: (
          recommended?: string
        ) => `import type { PageServerLoad } from './$types'
  import { basehub } from 'basehub'
  
  export const load: PageServerLoad = async () => {
  ${addSpacesToCodeBlock(recommended || exampleQuery, 2)}
  
    return {
      page_server_data: { data }
    }
  }`,
      },
      {
        filename: 'src/routes/+page.svelte',
        code: `<script lang="ts">
    import type { PageData } from './$types'
    export let data: PageData
  </script>
  
  <h1>Welcome to SvelteKit</h1>
  <pre>{JSON.stringify(data, null, 2)}</pre>`,
      },
    ],
  },
  remix: {
    selectValue: 'Remix',
    language: 'javascript',
    snippets: [],
    disabled: true,
  },
  vue: {
    selectValue: 'Vue.js',
    language: 'javascript',
    snippets: [],
    disabled: true,
  },
}

export const exampleQuery = `const data = await basehub().query({
    __typename: true
  })`

export function parseInferredQuery(query: string, preSpaces?: number) {
  const lines = query.split('\n')
  const result: string[] = []
  lines.forEach((line) => {
    if (line.trim() === '') return
    result.push(line.replace('basehub()', 'const data = await basehub()'))
  })
  return addSpacesToCodeBlock(result.join('\n'), preSpaces ?? 0)
}

export function addSpacesToCodeBlock(code: string, preSpaces: number) {
  const lines = code.split('\n')
  const result: string[] = []
  lines.forEach((line) => {
    result.push(Array.from({ length: preSpaces + 1 }).join(' ') + line)
  })
  return result.join('\n')
}
